import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getCurrentUser, login, logout, refreshToken } from './api';
import { useAppDispatch } from '@/store/hooks';
import { setOpen, setMode } from '@/store/slices/loadingOverlaySlice';
import { useAuth } from '@/contexts/AuthContext';
import { clearLocalStoragePreserving } from '@/utils/localStoragePreserver';
import { useNavigate } from 'react-router-dom';
import { handleApiError } from '@/utils/errorHandler';
import { getStoredAccessToken } from './accessTokenStore';
import { sessionManager } from './sessionManager';

/**
 * useLogin - hook for login mutation
 */
export function useLogin() {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const { setUser } = useAuth();
  // keep hook call for consistent React usage patterns; navigation is not currently used here
  useNavigate();

  return useMutation({
    mutationFn: async (payload: { login: string; password: string }) => {
      dispatch(setMode('default'));
      dispatch(setOpen(true));
      return login(payload);
    },
    onSuccess: userData => {
      // Clear all React Query cache to prevent showing previous user's data
      queryClient.clear();

      // Update query cache with basic user data
      queryClient.setQueryData(['currentUser'], userData);

      // Transform and update AuthContext (full profile already loaded)
      const user = {
        id: userData.id,
        email: userData.email,
        username: userData.username,
        first_name: userData.first_name,
        last_name: userData.last_name,
        current_study: userData.current_study,
        roles: userData.roles,
        permitted_studies: userData.permitted_studies,
      };
      setUser(user);

      // Update query cache with full profile
      queryClient.setQueryData(['currentUser'], userData);

      // Clear reauthRequired so the Session expired modal does not appear after successful login
      if (sessionManager.isReauthRequired()) {
        sessionManager.notifyReauthSuccess();
      }

      // Close loading modal immediately after successful login
      dispatch(setOpen(false));
      dispatch(setMode('default'));
    },
    onError: error => {
      dispatch(setOpen(false));
      dispatch(setMode('default'));

      // Handle error through errorHandler
      handleApiError(error, 'Login');
    },
  });
}

/**
 * useLogout - hook for logout mutation
 */
export function useLogout() {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const { reset } = useAuth();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async () => {
      dispatch(setMode('logout'));
      dispatch(setOpen(true));
      return logout();
    },
    onSuccess: () => {
      // Clear all React Query cache to prevent showing previous user's data
      queryClient.clear();

      // Clear all localStorage data (mappings, upload queues, user-specific settings, etc.)
      clearLocalStoragePreserving({ preserveKeyPrefixes: ['activeRole_'] });

      // Keep modal open for minimum 2 seconds to let animation complete
      setTimeout(() => {
        // Close modal and reset auth after animation delay
        dispatch(setOpen(false));
        dispatch(setMode('default'));
        reset();

        // Navigate to login with additional delay for smooth transition
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 400); // 400ms after modal closes (300ms close animation + 100ms buffer)
      }, 2000); // 2 seconds minimum display time
    },
    onError: error => {
      // Clear cache and localStorage even on error to ensure clean state
      queryClient.clear();
      clearLocalStoragePreserving({ preserveKeyPrefixes: ['activeRole_'] });

      // Keep modal open briefly even on error to avoid abrupt closing
      setTimeout(() => {
        dispatch(setOpen(false));
        dispatch(setMode('default'));
        reset();

        // Handle error through errorHandler
        handleApiError(error, 'Logout');

        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 400);
      }, 1000); // 1 second display time for error case
    },
  });
}

/**
 * useChangeStudy - hook for changing current study
 */
export function useChangeStudy() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (studyId: number) => {
      // Call refresh token API with new study ID to switch study context
      await refreshToken(studyId);
      return { success: true };
    },
    onSuccess: () => {
      // Note: Session store update is now handled by the caller (SessionProvider)
      // This prevents double updates and ensures proper token expiration parsing

      // Invalidate user cache to fetch updated user data with new study
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });

      // Invalidate all study-related caches to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['studyFiles'] });
      queryClient.invalidateQueries({ queryKey: ['csvFiles'] });
      queryClient.invalidateQueries({ queryKey: ['whitelistedStudyFiles'] });
      queryClient.invalidateQueries({ queryKey: ['whitelists'] });
      queryClient.invalidateQueries({ queryKey: ['eventTrails'] });

      // Small delay to ensure all cache invalidation is complete
      setTimeout(() => {
        // Navigate to home page using React Router (soft navigation)
        navigate('/', { replace: true });
      }, 100);
    },
    onError: error => {
      // Handle error through errorHandler
      handleApiError(error, 'Change Study');
    },
  });
}

/**
 * useCurrentUser - hook for fetching current user
 */
export function useCurrentUser(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      try {
        const user = await getCurrentUser();
        return user;
      } catch (error) {
        // Cold-start / return-from-idle case:
        // - access_token cookie may be missing/expired
        // - refresh_token cookie may still be valid
        // - backend refresh requires an access token (cookie or Authorization header)
        //
        // If the user has authenticated before (or we have a stored fallback access token),
        // try a single silent refresh and then retry the profile request once.
        if (error instanceof Error && error.message === 'Unauthorized') {
          const hasAuthenticatedBefore =
            typeof window !== 'undefined' &&
            window.localStorage.getItem('hasAuthenticated') === 'true';
          const hasFallbackAccessToken = !!getStoredAccessToken();

          if (hasAuthenticatedBefore || hasFallbackAccessToken) {
            try {
              await refreshToken();
              return await getCurrentUser();
            } catch {
              // Fall through to the normal error path.
            }
          }
        }

        if (
          !(error instanceof Error && error.message === 'Unauthorized') &&
          !(error instanceof Error && error.message === 'Failed to fetch user')
        ) {
          handleApiError(error, 'Current User');
        }
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
    ...options,
  });
}
